import React, { useState } from 'react';
import { motion } from 'framer-motion';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    commodity: '',
    message: '',
  });

  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null,
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, error: null });

    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ submitting: false, submitted: true, error: null });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          commodity: '',
          message: '',
        });
      } else {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (error) {
      setStatus({
        submitting: false,
        submitted: false,
        error: error.message,
      });
    }
  };

  const contactInfo = [
    {
      icon: '📍',
      title: 'Location',
      content: 'London, United Kingdom',
    },
    {
      icon: '📧',
      title: 'Email',
      content: 'info@markhor-group.com',
      link: 'mailto:info@markhor-group.com',
    },
    {
      icon: '🕐',
      title: 'Business Hours',
      content: 'Monday - Friday: 9:00 AM - 6:00 PM GMT',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-accent text-white pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Get in touch to discuss your commodity requirements
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-3xl font-bold text-primary mb-6">
                  Send Us a Message
                </h2>

                {status.submitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6"
                  >
                    <p className="font-semibold">Thank you for your inquiry!</p>
                    <p className="text-sm">We will get back to you soon.</p>
                  </motion.div>
                )}

                {status.error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6"
                  >
                    <p className="font-semibold">Error!</p>
                    <p className="text-sm">{status.error}</p>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent transition duration-200"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent transition duration-200"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent transition duration-200"
                        placeholder="+44 123 456 7890"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent transition duration-200"
                        placeholder="Your Company Ltd"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Commodity of Interest *
                    </label>
                    <select
                      name="commodity"
                      value={formData.commodity}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent transition duration-200"
                    >
                      <option value="">Select a commodity</option>
                      <option value="Fertiliser (Potash MOP)">Fertiliser (Potash MOP)</option>
                      <option value="Thermal Coal">Thermal Coal</option>
                      <option value="Wheat">Wheat</option>
                      <option value="Metals (Titanium)">Metals (Titanium)</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Your Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent transition duration-200"
                      placeholder="Please provide details about your requirements..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={status.submitting}
                    className={`w-full font-bold py-4 rounded-lg transition duration-300 transform hover:scale-105 ${
                      status.submitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-secondary hover:bg-secondary-dark text-primary shadow-lg'
                    }`}
                  >
                    {status.submitting ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin h-5 w-5 mr-3"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      'Send Inquiry'
                    )}
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-bold text-primary mb-6">
                  Get in Touch
                </h2>
                <p className="text-lg text-gray-700 mb-8">
                  We're here to answer your questions and discuss how we can support your commodity requirements. Reach out to us through any of the channels below.
                </p>

                <div className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300"
                    >
                      <div className="flex items-start">
                        <div className="text-4xl mr-4">{info.icon}</div>
                        <div>
                          <h3 className="font-bold text-primary text-lg mb-1">
                            {info.title}
                          </h3>
                          {info.link ? (
                            <a
                              href={info.link}
                              className="text-gray-700 hover:text-secondary transition duration-200"
                            >
                              {info.content}
                            </a>
                          ) : (
                            <p className="text-gray-700">{info.content}</p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-gradient-to-br from-primary to-accent text-white rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-4">Why Contact Us?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-secondary mr-2 text-xl">✓</span>
                    <span>Quick response to your inquiries</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2 text-xl">✓</span>
                    <span>Expert advice on commodity selection</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2 text-xl">✓</span>
                    <span>Competitive quotations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2 text-xl">✓</span>
                    <span>Flexible terms and solutions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-secondary mr-2 text-xl">✓</span>
                    <span>Transparent communication</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map or Additional CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center bg-gray-50 rounded-2xl p-12"
          >
            <h2 className="text-3xl font-bold text-primary mb-4">
              Serving Clients Globally
            </h2>
            <p className="text-xl text-gray-700 mb-6">
              From our base in London, we serve commodity markets across Europe, Asia, and Africa with reliable supply chain solutions.
            </p>
            <div className="flex justify-center space-x-8 text-center">
              <div>
                <p className="text-4xl font-bold text-secondary">🇪🇺</p>
                <p className="text-gray-700 font-semibold mt-2">Europe</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-secondary">🌏</p>
                <p className="text-gray-700 font-semibold mt-2">Asia</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-secondary">🌍</p>
                <p className="text-gray-700 font-semibold mt-2">Africa</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
